<script lang="ts" setup>
import { computed, onMounted, ref } from 'vue';
import { CdrLink } from '@rei/cedar';
import { win } from '@rei/browser-shim';
import FrequentlyAskedQuestions from '@rei/frequently-asked-questions';
import { setCTAEntries } from '@rei/rich-text-renderer';
import HelpChat from '@returns-page/components/gladly-chat/HelpChat.vue';
import InPageNav from '@returns-page/components/in-page-nav/InPageNav.vue';
import ReturnsLead from '@returns-page/components/returns-lead/ReturnsLead.vue';
import ResourceSection from '@returns-page/components/resource-section/ResourceSection.vue';
import Formatters from '@returns-page/utils/formatters.mts';
import type { ReturnsPageProps, SectionItem } from '@returns-page/types/index.mts';
import DOMTools from '@utils/dom-tools.mts';

const props = withDefaults(defineProps<ReturnsPageProps>(), {
  showReturnsChatLinks: false,
  gladlyChatLinks: () => [],
});

const timeout = ref<NodeJS.Timeout | null>(null);
const showHelpChat = ref(false);

const leadModel = computed(() => (props.returnsContent
  ? {
    text: {
      heading: props.returnsContent.heading,
      description: props.returnsContent.description,
    },
  }
  : null));

const sectionItems = props.returnsContent?.section.map((item) => item);

const getCtaEntries = (item: SectionItem) => item.body.ctaContents.map((cta) => ({
  ...cta.link,
  uid: cta.entryId,
  isSecondary: Boolean(cta.secondary),
}));

const resourceSections = computed(() => (
  sectionItems.map((item) => {
    const ctaEntries = getCtaEntries(item);

    if (ctaEntries.length) {
      setCTAEntries(ctaEntries);
    }

    return {
      title: item.heading,
      body: DOMTools.richTextToHtml(item.body.json, { sanitize: true }),
      resourceHubSubsections: item.resourceHubSubsections,
      sectionId: Formatters.textToID(item.heading, { slashesToDash: true }),
    };
  })
));

const navModel = computed(() => resourceSections.value?.map((item) => ({
  title: item.title,
  id: Formatters.textToID(item.title, { slashesToDash: true }),
})));

const faqModel = computed(() => {
  const faqSection = props.returnsContent?.faqSection[0];
  const faqs: { question: string; answer: string }[] = [];

  faqSection.faqs.forEach((qModel) => {
    faqs.push({
      question: qModel.question,
      answer: DOMTools.richTextToHtml(qModel.answer, { sanitize: true }),
    });
  });

  return {
    title: faqSection.title,
    heading: faqSection.title,
    questionsAndAnswers: faqs,
    id: Formatters.textToID(faqSection.title),
  };
});

const renderingMap = computed(() => ({
  faq: Boolean(faqModel.value),
  lead: Boolean(leadModel.value),
  nav: Boolean(navModel.value?.length),
  content: Boolean(resourceSections.value?.length),
}));

onMounted(async () => {
  setTimeout(() => {
    const anchor = win.location.href.split('#')[1];

    DOMTools.scroll(`#${anchor}`);
  }, 600);

  // Start metrics
  const { metrics } = win;

  /* c8 ignore start */
  if (metrics && typeof metrics?.view === 'function') {
    const returnsPageData = {
      siteId: 'rei',
      pageName: 'landing page_returns',
      templateType: 'landing page',
      contentType: 'commerce',
      subSection1: 'returns',
      channel: 'rei_commerce',
    };

    metrics.view(returnsPageData);
    metrics.addClickListenersFromSelector();
  }
  /* c8 ignore end */

  timeout.value = setInterval(() => {
    // Check for gladly chat container to update the chat links
    const gladlyContainer = document.querySelector(`#${props.gladlyClasses?.chatContainerId}`);

    if (gladlyContainer) {
      showHelpChat.value = props.showReturnsChatLinks || false;
      gladlyContainer.setAttribute(
        'style',
        `visibility: ${showHelpChat.value ? 'hidden' : 'visible'} !important;`,
      );
      clearInterval(timeout.value!);
    }
  }, 1000);
});
</script>

<template>
  <div data-ui="returns-page">
    <div
      v-if="renderingMap.lead"
      data-ui="returns-lead"
    >
      <returns-lead v-bind="leadModel" />
    </div>
    <div class="return-instructions__grid">
      <div
        v-if="renderingMap.nav"
        class="return-instructions__grid--nav"
        data-ui="returns-nav"
      >
        <in-page-nav
          :nav-model="navModel"
          :faq-title="faqModel.heading"
        />
      </div>
      <div
        v-if="renderingMap.content"
        class="return-instructions__grid--main"
        data-ui="returns-content"
      >
        <section
          v-for="(resource, index) in resourceSections"
          :key="Formatters.textToID(resource.title)"
          data-ui="returns-section"
        >
          <resource-section
            :resource="resource"
            :index="index"
          />
        </section>
        <frequently-asked-questions
          v-if="renderingMap.faq"
          id="frequently-asked-questions"
          data-ui="returns-faq"
          :data-index="resourceSections.length"
          :faq="faqModel"
        />
        <cdr-link
          class="return-to-nav"
          aria-label="Return to Returns and Exchanges page navigation"
          href="#in-page-nav"
        >
          Return to Returns & Exchanges page navigation
        </cdr-link>
        <div id="bottom-of-page" />
      </div>
    </div>
    <help-chat
      v-if="showHelpChat"
      :gladly-classes="gladlyClasses"
    />
  </div>
</template>

<style lang="scss">
@import '@rei/cdr-tokens/dist/rei-dot-com/scss/cdr-tokens.scss';
@import '@rei/cdr-component-variables/dist/scss/index.scss';

html {
  @media (prefers-reduced-motion: no-preference) {
    scroll-behavior: smooth;
  }
}

a {
  @include cdr-link-base-mixin;

  display: inline;
}

// Links to be styled as Cedar buttons from ContentStack
.rei-cms__cta--block {
  @include cdr-button-base-mixin;
  @include cdr-button-primary-mixin;

  margin-right: $cdr-space-one-x;
  margin-bottom: $cdr-space-one-x;
}

.rei-cms__cta--secondary {
  @include cdr-button-secondary-mixin;
}

.faq-container {
  padding: 1.6rem;

  &__content {
    padding: unset;

    & .heading {
      padding-bottom: 3.2rem;
    }

    & .faq-contact-btn {
      @include cdr-button-base-mixin;
      @include cdr-button-secondary-mixin;

      justify-content: center;
      width: 100%;
      margin-bottom: $cdr-space-three-quarter-x;
    }
    & .faq-return-btn {
      @include cdr-button-base-mixin;
      @include cdr-button-primary-mixin;

      margin-bottom: $cdr-space-half-x;
    }
  }
}

.return-instructions {
  &__grid {
    @include cdr-grid-base-mixin;

    display: initial;
    grid-template-columns: 1fr;

    @include cdr-sm-mq-up {
      @include cdr-container;

      display: grid;
      grid-template-columns: 1fr 3fr;
    }

    &--nav {
      @include cdr-sm-mq-up {
        border-right: 2px solid $cdr-color-border-primary;
      }
    }

    &--main {
      @include cdr-sm-mq-up {
        padding: $cdr-space-two-x;
      }

      h2 {
        text-align: left !important;
      }
    }
  }
}

.faq-spacing {
  margin-top: $cdr-space-one-x;
}

#faq-0-collapsible > p > div {
  display: flex;
  flex-direction: column;
}

#faq-0-collapsible > p > ul > li {
  @include cdr-list-base-mixin;
  @include cdr-list-unordered-mixin;
}

ol li::marker {
  font-weight: bold;
}

.contact-svg {
  margin-right: $cdr-space-half-x;
}

// hides the 'Return to Returns and Exchanges page navigation link'
// so it is not visible but keyboard users can select it
// to return to the nav (really just for mobile view)
.return-to-nav {
  clip-path: circle(0) !important;
}

.return-to-nav:focus {
  padding: $cdr-space-half-x;
  text-transform: uppercase;
  text-decoration: none;
  clip-path: none !important;
  color: $cdr-color-border-tab-keyline-active;
}

.resource-section__body {
  & ul {
    @include cdr-list-unordered-mixin;

    & li {
      @include cdr-list-base-mixin;
      @include cdr-list-unordered-mixin;
    }
  }

  & ol {
    @include cdr-list-base-mixin;
    @include cdr-list-ordered-mixin;
  }
}

#gift-returns ol {
  margin-left: $cdr-space-quarter-x;
}

#gift-returns {
  & .ordered-marker-type {
    margin: $cdr-space-one-x 0;
  }
}

#gift-returns ul {
  @include cdr-list-unordered-mixin;

  margin-top: $cdr-space-one-x;
  padding-left: revert !important;
}
</style>
